import { Link } from 'react-router';
import {
  GuideLayout,
  GuideSection,
  GuideStep,
  GuideSteps,
  GuideTip,
} from '@/components/guides/guide-layout';
import { PhaseFiveGuideNotes } from '@/components/guides/phase-five-guide-notes';

const inlineLink =
  'font-medium text-brand-600 underline decoration-brand-300 underline-offset-2';

export function DashcamPotholeEvidenceGuide() {
  return (
    <GuideLayout
      slug="dashcam-pothole-evidence"
      category="Evidence"
      categoryColor="accent"
      title="Dashcam Evidence for Pothole Claims"
      seoTitle="Dashcam Evidence for Pothole Claims | Fixtyer"
      description="Preserve original dashcam footage and metadata, export useful copies and photograph road damage without creating another danger."
      readTime="7 min"
      updatedDate="2026-07-19"
      reviewedDate="2026-07-19"
      datePublished="2026-07-19"
      reviewedFor="England and Wales"
      contentOwner="Elevyn Technology Group Limited editorial team"
      sources={[
        {
          name: 'GOV.UK digital imaging procedure',
          url: 'https://www.gov.uk/government/publications/digital-investigations-digital-imaging-and-multimedia-procedure/digital-imaging-and-multimedia-procedure-v30',
        },
        {
          name: 'The Highway Code: motorway stopping',
          url: 'https://www.gov.uk/guidance/the-highway-code/motorways-253-to-273',
        },
        {
          name: 'The Highway Code: breakdowns',
          url: 'https://www.gov.uk/guidance/the-highway-code/breakdowns-and-incidents-274-to-287',
        },
      ]}
      atAGlance={[
        {
          label: 'Answer first',
          value:
            'Preserve the original dashcam file and its metadata before making clips, screenshots or compressed copies.',
        },
        {
          label: 'Safety first',
          value:
            'Never stop or walk on a motorway or live carriageway to photograph a defect.',
        },
        {
          label: 'Explain the clock',
          value:
            'If the camera time, date or time zone is wrong, record the known offset rather than altering the original.',
        },
      ]}
      relatedGuides={[
        {
          slug: 'photographing-pothole-damage',
          title: 'Photographing Pothole Damage',
          readTime: '6 min',
        },
        {
          slug: 'documenting-vehicle-damage',
          title: 'Document Vehicle Damage',
          readTime: '5 min',
        },
        {
          slug: 'national-highways-pothole-claims',
          title: 'National Highways Claims',
          readTime: '7 min',
        },
      ]}
    >
      <GuideSteps>
        <GuideStep number={1} title="Protect the recording before it loops">
          Stop the dashcam from overwriting the incident if the device allows this safely. Export
          the complete incident sequence in its native format, including the approach and aftermath,
          and keep the original memory card unchanged where practical.
        </GuideStep>
        <GuideStep number={2} title="Keep one master and one working copy">
          Store the exported original securely and do not edit it. Make a separate copy for
          trimming, screenshots or sharing. Retain filenames, folder structure and sidecar files
          because they may carry time, GPS or device metadata.
        </GuideStep>
        <GuideStep number={3} title="Write a short provenance note">
          Record the device, vehicle, export date, person exporting, original filename, known clock
          offset and storage location. A file hash can help demonstrate that a later copy is
          unchanged, but it does not prove what the footage depicts.
        </GuideStep>
      </GuideSteps>

      <GuideSection title="What useful footage can show" icon={<span aria-hidden="true">▶</span>}>
        <ul className="list-disc space-y-2 pl-5">
          <li>direction of travel and surrounding landmarks;</li>
          <li>traffic, weather and visibility around the incident;</li>
          <li>the vehicle’s approach and immediate reaction;</li>
          <li>a time sequence that can be compared with recovery and repair records.</li>
        </ul>
        <GuideTip variant="info" title="Keep the inference modest">
          Footage may help identify location and sequence. It does not by itself establish a
          pothole&apos;s dimensions, how long it existed, what an authority knew or legal liability.
        </GuideTip>
      </GuideSection>

      <GuideSection title="Safe road and vehicle photographs" icon={<span aria-hidden="true">⚠</span>}>
        <p className="mb-4">
          Photograph the vehicle damage after reaching a safe place. For a defect, use images only
          if they can be taken lawfully from an existing safe position away from moving traffic.
          Do not enter the road, obstruct traffic or ask another person to stand in danger.
        </p>
        <p>
          Link to existing depth on{' '}
          <Link to="/guides/photographing-pothole-damage" className={inlineLink}>
            context, scale and location photographs
          </Link>{' '}
          rather than relying on one close-up image.
        </p>
      </GuideSection>

      <PhaseFiveGuideNotes />
    </GuideLayout>
  );
}
